import { failure, Result, success } from "src/types/result";

type FileType =
  | "pdf"
  | "heic"
  | "heif"
  | "png"
  | "jpeg"
  | "gif"
  | "webp"
  | "bmp"
  | "unknown";

type MimeType =
  | "application/pdf"
  | "image/heic"
  | "image/heif"
  | "image/heic-sequence"
  | "image/heif-sequence"
  | "image/png"
  | "image/jpeg"
  | "image/jpg"
  | "image/gif"
  | "image/webp"
  | "image/bmp"
  | "image/x-ms-bmp"
  | "application/octet-stream";

const MimeMap: Record<MimeType, FileType> = {
  "application/pdf": "pdf",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/heic-sequence": "heic",
  "image/heif-sequence": "heif",
  "image/png": "png",
  "image/jpeg": "jpeg",
  "image/jpg": "jpeg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/bmp": "bmp",
  "image/x-ms-bmp": "bmp",
  "application/octet-stream": "unknown",
};

const ExtensionMap: Record<string, FileType> = {
  pdf: "pdf",
  heic: "heic",
  heif: "heif",
  png: "png",
  jpg: "jpeg",
  jpeg: "jpeg",
  gif: "gif",
  webp: "webp",
  bmp: "bmp",
};

type File = {
  fileType: FileType;
  blob: Blob;
};

/**
 * Fetch file and detect type from Content-Type header
 */
export async function fetchAndDetectFileType(
  url: string,
  fileName: string
): Promise<Result<File>> {
  try {
    let blob: Blob | undefined = undefined;
    let response: Response;
    try {
      response = await fetch(url);
      if (!response.ok) {
        return failure("Unable to fetch file: " + response.statusText);
      }

      blob = await response.blob();
    } catch (error) {
      // unable to read file
      console.error(error);
      return failure("Error fetching file");
    }

    // Get MIME type from Content-Type header
    const contentType = response.headers.get("Content-Type");

    const mimeType =
      contentType?.split(";")[0]?.trim() || "application/octet-stream";

    // Map MIME type to file type
    const type = mimeTypeToFileType(mimeType);
    if (type === "unknown") {
      return success({
        fileType: detectFileTypeFromExtension(fileName),
        blob,
      });
    }

    return success({
      fileType: type,
      blob,
    });
  } catch (error) {
    console.error("Error detecting file type", error);
    // Fallback to extension
    return failure("Error fetching file");
  }
}

function mimeTypeToFileType(mimeType: string): FileType {
  return MimeMap[mimeType.toLowerCase() as MimeType] ?? "unknown";
}

function detectFileTypeFromExtension(filename: string): FileType {
  const ext = filename.toLowerCase().split(".").pop() || "";
  return ExtensionMap[ext] ?? "unknown";
}
